// src/pages/admin/AdminSubscription.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { subscriptionRequestService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast } from '../../utils/toastUtils';

const AdminSubscription = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  // Request form state
  const [requestForm, setRequestForm] = useState({
    requestType: 'extension',
    requestedDuration: 1,
    durationType: 'months',
    message: '',
    priority: 'medium'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load subscription and requests in parallel
      const [subscriptionResponse, requestsResponse] = await Promise.allSettled([
        subscriptionService.getMyCompanySubscription(),
        subscriptionRequestService.getMyCompanyRequests()
      ]);

      // Handle subscription data
      if (subscriptionResponse.status === 'fulfilled' && subscriptionResponse.value.success) {
        setSubscription(subscriptionResponse.value.data);
      }

      // Handle requests data
      if (requestsResponse.status === 'fulfilled' && requestsResponse.value.success) {
        setRequests(requestsResponse.value.data || []);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      showErrorToast('Erreur lors du chargement des données d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for duration-based requests
    if (['new', 'extension', 'renewal', 'suspension'].includes(requestForm.requestType)) {
      if (requestForm.requestedDuration < 1) {
        showErrorToast('La durée doit être d\'au moins 1');
        return;
      }
      
      if (requestForm.requestedDuration > 365 && requestForm.durationType === 'days') {
        showErrorToast('La durée ne peut pas dépasser 365 jours');
        return;
      }
      
      if (requestForm.requestedDuration > 60 && requestForm.durationType === 'months') {
        showErrorToast('La durée ne peut pas dépasser 60 mois');
        return;
      }
    }
    
    // Validation for message-based requests
    if (['feature_request', 'support', 'upgrade', 'downgrade'].includes(requestForm.requestType)) {
      if (!requestForm.message.trim()) {
        showErrorToast('Un message détaillé est requis pour ce type de demande');
        return;
      }
    }

    const loadingToastId = showLoadingToast('Création de la demande en cours...');
    setRequestLoading(true);

    try {
      const response = await subscriptionRequestService.createRequest(requestForm);
      
      if (response.success) {
        updateToast(loadingToastId, 'Demande d\'abonnement créée avec succès', 'success');
        setShowRequestForm(false);
        setRequestForm({
          requestType: 'extension',
          requestedDuration: 1,
          durationType: 'months',
          message: '',
          priority: 'medium'
        });
        // Reload requests
        await loadData();
      } else {
        throw new Error(response.message || 'Erreur lors de la création de la demande');
      }
    } catch (error) {
      console.error('Error creating subscription request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la création de la demande d\'abonnement';
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      const response = await subscriptionRequestService.deleteRequest(requestId);
      
      if (response.success) {
        showSuccessToast('Demande supprimée avec succès');
        await loadData();
      } else {
        throw new Error(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression de la demande';
      showErrorToast(errorMessage);
    }
  };

  const getSubscriptionStatusType = () => {
    if (!subscription || subscription.status !== 'active') return 'danger';
    if (subscription.daysRemaining <= 3) return 'danger';
    if (subscription.daysRemaining <= 7) return 'warning';
    return 'success';
  };

  const getRequestStatusBadge = (status) => {
    const statusConfig = {
      pending: { type: 'warning', text: 'En attente' },
      approved: { type: 'success', text: 'Approuvée' },
      rejected: { type: 'danger', text: 'Rejetée' }
    };
    return statusConfig[status] || { type: 'secondary', text: status };
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      urgent: { type: 'danger', text: 'Urgent' },
      high: { type: 'warning', text: 'Élevée' },
      medium: { type: 'info', text: 'Moyenne' },
      low: { type: 'secondary', text: 'Faible' }
    };
    return priorityConfig[priority] || { type: 'secondary', text: priority };
  };

  const canCreateRequest = () => {
    // Check if there's already a pending request
    const hasPendingRequest = requests.some(req => req.status === 'pending');
    return !hasPendingRequest;
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon Abonnement</h1>
          <p className="text-gray-600 mt-1">Gérez votre abonnement et vos demandes</p>
        </div>
        <div className="flex items-center space-x-3">
          {!subscription && (
            <button
              onClick={() => setShowRequestForm(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Demander un Abonnement
            </button>
          )}
          {canCreateRequest() && subscription && (
            <button
              onClick={() => setShowRequestForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Demander une Extension
            </button>
          )}
          {!canCreateRequest() && (
            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-md">
              Demande en cours de traitement
            </div>
          )}
        </div>
      </div>

      {/* Subscription Status Card */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Statut de l'Abonnement</h2>
            {subscription && (
              <StatusBadge 
                status={subscription.status === 'active' ? `Actif (${subscription.daysRemaining || 0}j restants)` : 'Inactif'} 
                type={getSubscriptionStatusType()} 
              />
            )}
          </div>

          {subscription ? (
            <>
              {/* Progress Bar for Days Remaining */}
              {subscription.status === 'active' && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Progression de l'abonnement</span>
                    <span className="text-sm text-gray-500">
                      {subscription.daysRemaining || 0} jours restants
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        subscription.daysRemaining <= 7 ? 'bg-red-500' : 
                        subscription.daysRemaining <= 30 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ 
                        width: `${Math.max(5, Math.min(100, (subscription.daysRemaining / 365) * 100))}%` 
                      }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 100-4 2 2 0 000 4zm0 0v4a2 2 0 002 2h6a2 2 0 002-2v-4" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Date de début</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.startDate 
                          ? new Date(subscription.startDate).toLocaleDateString('fr-FR')
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Date d'expiration</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.endDate 
                          ? new Date(subscription.endDate).toLocaleDateString('fr-FR')
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${
                      subscription.daysRemaining <= 7 ? 'bg-red-100' : 
                      subscription.daysRemaining <= 30 ? 'bg-yellow-100' : 'bg-green-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        subscription.daysRemaining <= 7 ? 'text-red-600' : 
                        subscription.daysRemaining <= 30 ? 'text-yellow-600' : 'text-green-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Jours restants</p>
                      <p className={`text-lg font-semibold ${
                        subscription.daysRemaining <= 7 ? 'text-red-600' : 
                        subscription.daysRemaining <= 30 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {subscription.daysRemaining || 0} jours
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun abonnement actif</h3>
              <p className="text-gray-500 mb-4">Vous n'avez pas d'abonnement actif pour le moment.</p>
              <button
                onClick={() => setShowRequestForm(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Demander un abonnement
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Requests */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Mes Demandes d'Abonnement</h2>
          
          {requests.length > 0 ? (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request._id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <StatusBadge 
                        status={getRequestStatusBadge(request.status).text}
                        type={getRequestStatusBadge(request.status).type}
                        size="sm"
                      />
                      <StatusBadge 
                        status={getPriorityBadge(request.priority).text}
                        type={getPriorityBadge(request.priority).type}
                        size="sm"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleDeleteRequest(request._id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Type</p>
                      <p className="text-sm text-gray-900 capitalize">{request.requestType}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Durée demandée</p>
                      <p className="text-sm text-gray-900">
                        {request.requestedDuration} {request.durationType}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Demandé par</p>
                      <p className="text-sm text-gray-900">{request.requestedByName}</p>
                    </div>
                  </div>
                  
                  {request.message && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Message</p>
                      <p className="text-sm text-gray-700">{request.message}</p>
                    </div>
                  )}
                  
                  {/* Admin Response Section */}
                  {request.adminResponse && (
                    <div className={`rounded-lg p-4 mt-4 ${
                      request.status === 'approved' ? 'bg-green-50 border border-green-200' :
                      request.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                      'bg-gray-50 border border-gray-200'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${
                          request.status === 'approved' ? 'bg-green-100' :
                          request.status === 'rejected' ? 'bg-red-100' :
                          'bg-gray-100'
                        }`}>
                          {request.status === 'approved' ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : request.status === 'rejected' ? (
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className={`text-sm font-medium ${
                              request.status === 'approved' ? 'text-green-800' :
                              request.status === 'rejected' ? 'text-red-800' :
                              'text-gray-800'
                            }`}>
                              {request.status === 'approved' ? 'Demande approuvée' :
                               request.status === 'rejected' ? 'Demande rejetée' :
                               'Réponse de l\'administrateur'}
                            </p>
                            {request.reviewedAt && (
                              <span className="text-xs text-gray-500">
                                {new Date(request.reviewedAt).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${
                            request.status === 'approved' ? 'text-green-700' :
                            request.status === 'rejected' ? 'text-red-700' :
                            'text-gray-700'
                          }`}>
                            {request.adminResponse}
                          </p>
                          {request.reviewedByName && (
                            <p className="text-xs text-gray-500 mt-2">
                              Par {request.reviewedByName}
                            </p>
                          )}
                          
                          {/* Show execution status if approved */}
                          {request.status === 'approved' && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span className="text-sm font-medium text-blue-800">
                                    {request.executed ? 'Extension appliquée' : 'En attente d\'application'}
                                  </span>
                                </div>
                                {request.executed && request.executedAt && (
                                  <span className="text-xs text-blue-600">
                                    Le {new Date(request.executedAt).toLocaleDateString('fr-FR')}
                                  </span>
                                )}
                              </div>
                              {request.executed && request.executionNotes && (
                                <p className="text-sm text-blue-700 mt-2">
                                  {request.executionNotes}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending Status Indicator */}
                  {request.status === 'pending' && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-yellow-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-yellow-800">
                          Demande en cours de traitement
                        </span>
                      </div>
                      <p className="text-sm text-yellow-700 mt-1">
                        Votre demande est en attente de révision par l'équipe administrative.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-500">Aucune demande d'abonnement</p>
            </div>
          )}
        </div>
      </div>

      {/* Request Form Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Nouvelle Demande d'Abonnement
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Remplissez le formulaire pour soumettre votre demande
                  </p>
                </div>
                <button
                  onClick={() => setShowRequestForm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleRequestSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de demande *
                  </label>
                  <select
                    value={requestForm.requestType}
                    onChange={(e) => setRequestForm({...requestForm, requestType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  >
                    <option value="new">Nouvel abonnement</option>
                    <option value="extension">Extension d'abonnement</option>
                    <option value="renewal">Renouvellement</option>
                    <option value="upgrade">Mise à niveau</option>
                    <option value="downgrade">Réduction de service</option>
                    <option value="suspension">Suspension temporaire</option>
                    <option value="cancellation">Annulation</option>
                    <option value="feature_request">Demande de fonctionnalité</option>
                    <option value="support">Support technique</option>
                  </select>
                  
                  {/* Request Type Description */}
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                      {requestForm.requestType === 'new' && 'Demande d\'activation d\'un nouvel abonnement pour votre entreprise.'}
                      {requestForm.requestType === 'extension' && 'Prolongation de votre abonnement actuel. La durée sera ajoutée à votre période existante.'}
                      {requestForm.requestType === 'renewal' && 'Renouvellement de votre abonnement après expiration.'}
                      {requestForm.requestType === 'upgrade' && 'Amélioration de votre plan d\'abonnement avec plus de fonctionnalités.'}
                      {requestForm.requestType === 'downgrade' && 'Réduction de votre plan d\'abonnement avec moins de fonctionnalités.'}
                      {requestForm.requestType === 'suspension' && 'Suspension temporaire de votre abonnement pour une période définie.'}
                      {requestForm.requestType === 'cancellation' && 'Annulation définitive de votre abonnement.'}
                      {requestForm.requestType === 'feature_request' && 'Demande d\'ajout de nouvelles fonctionnalités à votre abonnement.'}
                      {requestForm.requestType === 'support' && 'Demande d\'assistance technique ou de support.'}
                    </p>
                  </div>
                </div>

                {/* Duration Section - Only show for relevant request types */}
                {['new', 'extension', 'renewal', 'suspension'].includes(requestForm.requestType) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {requestForm.requestType === 'extension' ? 'Durée à ajouter *' : 
                         requestForm.requestType === 'suspension' ? 'Durée de suspension *' : 'Durée *'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={requestForm.durationType === 'days' ? 365 : requestForm.durationType === 'months' ? 60 : 10}
                        value={requestForm.requestedDuration}
                        onChange={(e) => setRequestForm({...requestForm, requestedDuration: parseInt(e.target.value) || 1})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unité *
                      </label>
                      <select
                        value={requestForm.durationType}
                        onChange={(e) => setRequestForm({...requestForm, durationType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="days">Jours</option>
                        <option value="months">Mois</option>
                        <option value="years">Années</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Extension Information */}
                {requestForm.requestType === 'extension' && subscription && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Extension d'abonnement
                        </p>
                        <p className="text-xs text-blue-600">
                          {subscription.endDate 
                            ? `Votre abonnement expire le ${new Date(subscription.endDate).toLocaleDateString('fr-FR')}. L'extension sera ajoutée à cette date.`
                            : 'L\'extension sera ajoutée à votre période d\'abonnement actuelle.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priorité *
                  </label>
                  <select
                    value={requestForm.priority}
                    onChange={(e) => setRequestForm({...requestForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Élevée</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message justificatif {['feature_request', 'support', 'upgrade', 'downgrade', 'cancellation'].includes(requestForm.requestType) && '*'}
                  </label>
                  <textarea
                    value={requestForm.message}
                    onChange={(e) => setRequestForm({...requestForm, message: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder={
                      requestForm.requestType === 'feature_request' ? 'Décrivez la fonctionnalité que vous souhaitez...' :
                      requestForm.requestType === 'support' ? 'Décrivez votre problème ou question...' :
                      requestForm.requestType === 'upgrade' ? 'Expliquez pourquoi vous souhaitez une mise à niveau...' :
                      requestForm.requestType === 'downgrade' ? 'Expliquez pourquoi vous souhaitez réduire votre service...' :
                      requestForm.requestType === 'cancellation' ? 'Expliquez les raisons de l\'annulation...' :
                      'Expliquez les raisons de votre demande...'
                    }
                    required={['feature_request', 'support', 'upgrade', 'downgrade', 'cancellation'].includes(requestForm.requestType)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {['feature_request', 'support', 'upgrade', 'downgrade', 'cancellation'].includes(requestForm.requestType) 
                      ? 'Requis - Fournissez des détails pour traiter votre demande'
                      : 'Optionnel - Ajoutez des détails pour accélérer le traitement'
                    }
                  </p>
                </div>

                {/* Request Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        Résumé de la demande
                      </p>
                      <p className="text-xs text-blue-600">
                        Type: {
                          requestForm.requestType === 'new' ? 'Nouvel abonnement' :
                          requestForm.requestType === 'extension' ? 'Extension d\'abonnement' :
                          requestForm.requestType === 'renewal' ? 'Renouvellement' :
                          requestForm.requestType === 'upgrade' ? 'Mise à niveau' :
                          requestForm.requestType === 'downgrade' ? 'Réduction de service' :
                          requestForm.requestType === 'suspension' ? 'Suspension temporaire' :
                          requestForm.requestType === 'cancellation' ? 'Annulation' :
                          requestForm.requestType === 'feature_request' ? 'Demande de fonctionnalité' :
                          requestForm.requestType === 'support' ? 'Support technique' :
                          requestForm.requestType
                        }
                      </p>
                      {['new', 'extension', 'renewal', 'suspension'].includes(requestForm.requestType) && (
                        <p className="text-xs text-blue-600">
                          Durée: {requestForm.requestedDuration} {requestForm.durationType}
                          {requestForm.requestType === 'extension' && ' (à ajouter à l\'abonnement actuel)'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowRequestForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={requestLoading}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {requestLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Création...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Créer la demande
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscription;